<template>
    <validation-observer slim #default="{ invalid }">
        <form class="login-form" ref="loginForm" @submit.prevent="onSubmit(invalid)">
            <div class="login-form__row">
                <validation-provider rules="required|phone" #default="{ errors }">
                    <text-input
                        class="login-form__phone"
                        id="login_form_phone"
                        name="phone"
                        label="Телефон"
                        mask="phone"
                        v-model="phone"
                        :class="{
                            'has-error': isPhoneInvalid || errors.length,
                            'has-success': isPhoneConfirmed && !errors.length,
                        }"
                        :status="phoneStatus || errors[0]"
                        :readonly="isLoading || isCodeSent"
                        @input="onPhoneInput"
                        inputmode="numeric"
                        v-focus
                    ></text-input>
                </validation-provider>
            </div>

            <div class="login-form__row" v-if="captchaLoadError" role="alert">
                <p class="text-danger mb-2">{{ captchaLoadError }}</p>
                <button type="button" class="btn btn-link p-0" @click="retryCaptchaLoad">Повторить загрузку</button>
            </div>

            <div class="login-form__row" v-if="formMessage" role="alert">
                <p class="text-danger mb-0">{{ formMessage }}</p>
            </div>

            <div class="login-form__row">
                <div id="captcha-container-auth"></div>
            </div>

            <div class="login-form__row" v-if="!isCodeSent">
                <input type="hidden" name="csrf" :value="csrf">
                <button
                    class="btn btn-block btn-main"
                    type="button"
                    :disabled="!phone || invalid"
                    @click="sendCode"
                >
                    <span v-if="isLoading" class="spinner-border spinner-border-sm"/>
                    {{ sendButtonText }}
                </button>
            </div>

            <div class="login-form__row" v-if="isCodeSent">
                <validation-provider rules="required|smsCode" #default="{ errors }">
                    <text-input
                        class="login-form__code"
                        id="login_form_smscode"
                        name="smscode"
                        label="Код из смс"
                        v-model="code"
                        ref="code"
                        :class="{
                            'has-error': isCodeInvalid || errors.length,
                            'has-success': isAuthorized && !errors.length,
                        }"
                        :status="codeStatus || errors[0]"
                        :readonly="isLoading || isAuthorized"
                        @input="onCodeInput"
                        @keypress.native.prevent.enter="confirmCode"
                        inputmode="numeric"
                        :autocomplete="'one-time-code'"
                    ></text-input>
                </validation-provider>
            </div>

            <div class="login-form__row" v-if="isCodeSent">
                <button
                    class="btn btn-block btn-main"
                    type="button"
                    :disabled="!phone || !code || invalid"
                    @click="confirmCode"
                >
                    <span v-if="isLoading" class="spinner-border spinner-border-sm"/>
                    <template v-else>Войти</template>
                </button>
            </div>

            <div class="login-form__row" v-if="isCodeSent && !isAuthorized">
                <p class="mb-0" v-if="resendCountdown > 0">Отправить код ещё раз можно через {{ resendCountdown }} с</p>
                <button
                    v-else
                    type="button"
                    class="btn btn-link p-0"
                    :disabled="isLoading"
                    @click="sendCode"
                >
                    {{ sendStage ? sendButtonText : 'Отправить код ещё раз' }}
                </button>
            </div>

            <div class="login-form__row">
                <p>Нет аккаунта? <a href="/register/">Зарегистрироваться</a></p>
            </div>
        </form>
    </validation-observer>
</template>

<script>

import {LABS_SETTINGS} from '../../constants';
import {LAB_REDIRECT_URL} from '../../constants/labs';
import { smartCaptchaMixin } from '../../mixins/smart-captcha-mixin';
import { SMART_CAPTCHA_SITEKEY, SMART_CAPTCHA_LOAD_ERROR_TEXT } from '../../services/smart-captcha';
import { postForm, NO_CONNECTION_TEXT } from '../../services/form-request';

const CAPTCHA_CONTAINER_ID = 'captcha-container-auth';
const RESEND_DELAY_SEC = 60;
const CAPTCHA_CHECK_TIMEOUT_MS = 60000;

const SEND_STAGE = {
    NONE: '',
    CHECKING: 'checking',
    SENDING: 'sending',
};

const MESSAGES = {
    captchaNotPassed: 'Проверка «Я не робот» не пройдена. Попробуйте ещё раз',
    sendFailed: 'Не удалось отправить код. Попробуйте ещё раз',
    noConnection: NO_CONNECTION_TEXT,
    codeSent: 'На телефон отправлен код подтверждения',
};

export default {
    name: 'login-form',

    mixins: [smartCaptchaMixin],

    data() {
        return {
            csrf: BX.bitrix_sessid(),
            phone: '',
            phoneConfirmed: '',
            phoneStatus: '',
            isPhoneInvalid: false,
            isPhoneConfirmed: false,

            code: '',
            codeFocus: false,
            codeStatus: '',
            isCodeSent: false,
            isCodeInvalid: false,

            isAuthorized: false,
            isLoading: false,
            formMessage: '',
            sendStage: SEND_STAGE.NONE,
            resendCountdown: 0,
            resendTimer: null,
            captchaCheckTimer: null,

            widgetId: null,
            widgetPromise: null,
            modalElement: null,
        };
    },

    methods: {
        focusOnCode() {
            try {
                this.$refs.code.focus();
            } catch (error) {
                //
            }
        },

        /**
         * Обработка ввода телефона
         */
        onPhoneInput() {
            this.phoneStatus = '';
            this.formMessage = '';
            this.isPhoneInvalid = false;
            this.isPhoneConfirmed = false;
        },

        /**
         * Обработка ввода телефона
         */
        onCodeInput() {
            this.codeStatus = '';
            this.isCodeInvalid = false;
        },

        /**
         * Загрузить captcha.js (по требованию) и отрисовать невидимый виджет — один раз.
         * @return {Promise<number|null>} id виджета или null, если капча недоступна
         */
        prepareCaptcha() {
            if (this.widgetId !== null) return Promise.resolve(this.widgetId);
            if (this.widgetPromise) return this.widgetPromise;

            this.widgetPromise = this.ensureSmartCaptcha()
                .then((smartCaptcha) => (smartCaptcha ? this.renderCaptcha(smartCaptcha) : null))
                .finally(() => {
                    this.widgetPromise = null;
                });

            return this.widgetPromise;
        },

        renderCaptcha(smartCaptcha) {
            try {
                const widgetId = smartCaptcha.render(CAPTCHA_CONTAINER_ID, {
                    sitekey: SMART_CAPTCHA_SITEKEY,
                    invisible: true,
                    hideShield: true,
                    callback: this.onCaptchaToken,
                });
                smartCaptcha.subscribe(widgetId, 'challenge-hidden', this.onCaptchaChallengeHidden);
                smartCaptcha.subscribe(widgetId, 'network-error', this.onCaptchaNetworkError);
                this.widgetId = widgetId;
                return widgetId;
            } catch (error) {
                window.console.warn('[smart-captcha] render', error.message);
                this.onCaptchaNetworkError();
                return null;
            }
        },

        retryCaptchaLoad() {
            this.captchaLoadError = '';
            this.prepareCaptcha();
        },

        stopSending(message = '') {
            this.clearCaptchaCheckTimer();
            this.isLoading = false;
            this.sendStage = SEND_STAGE.NONE;
            this.formMessage = message;
        },

        /**
         * Этап «Проверяем…» не может висеть вечно: нет токена за CAPTCHA_CHECK_TIMEOUT_MS — разблокировать форму
         */
        startCaptchaCheckTimer() {
            this.clearCaptchaCheckTimer();
            this.captchaCheckTimer = setTimeout(() => {
                if (this.sendStage === SEND_STAGE.CHECKING) {
                    this.stopSending(MESSAGES.captchaNotPassed);
                }
            }, CAPTCHA_CHECK_TIMEOUT_MS);
        },

        clearCaptchaCheckTimer() {
            clearTimeout(this.captchaCheckTimer);
            this.captchaCheckTimer = null;
        },

        onCaptchaChallengeHidden() {
            if (this.sendStage !== SEND_STAGE.CHECKING) return;
            this.stopSending(MESSAGES.captchaNotPassed);
        },

        onCaptchaNetworkError() {
            this.stopSending();
            this.captchaLoadError = SMART_CAPTCHA_LOAD_ERROR_TEXT;
        },

        /**
         * Шаг 1: капча (невидимая). Код уходит только после токена — в onCaptchaToken.
         */
        sendCode() {
            if (this.isLoading || !this.phone || this.resendCountdown > 0) return;

            this.isLoading = true;
            this.formMessage = '';
            this.sendStage = SEND_STAGE.CHECKING;
            this.startCaptchaCheckTimer();

            this.prepareCaptcha().then((widgetId) => {
                if (widgetId === null) {
                    this.stopSending();
                    return;
                }
                window.smartCaptcha.reset(widgetId);
                window.smartCaptcha.execute(widgetId);
            });
        },

        /**
         * Шаг 2: токен получен — отправляем код.
         */
        onCaptchaToken(token) {
            if (this.sendStage !== SEND_STAGE.CHECKING) return;
            this.clearCaptchaCheckTimer();
            this.sendStage = SEND_STAGE.SENDING;
            this.sendSmsCode(token);
        },

        /**
         * Шаг 3: «код отправлен» — только по ответу сервера success.
         */
        sendSmsCode(token) {
            const formData = new FormData(this.$refs.loginForm);
            formData.set('csrf', BX.bitrix_sessid());
            formData.set('smart-token', token);

            postForm('/api/user/send-code/', formData)
                .then((result) => {
                    if (result.status !== 'success') {
                        this.stopSending(result.message || MESSAGES.sendFailed);
                        return;
                    }

                    this.stopSending();
                    this.isCodeSent = true;
                    this.phoneStatus = MESSAGES.codeSent;
                    this.phoneConfirmed = this.phone;
                    this.isPhoneConfirmed = true;
                    this.startResendCountdown(Number(result.retry_after) || RESEND_DELAY_SEC);
                })
                .catch(() => this.stopSending(MESSAGES.noConnection));
        },

        startResendCountdown(seconds) {
            clearInterval(this.resendTimer);
            this.resendCountdown = seconds;
            this.resendTimer = setInterval(() => {
                this.resendCountdown -= 1;
                if (this.resendCountdown <= 0) {
                    clearInterval(this.resendTimer);
                    this.resendTimer = null;
                }
            }, 1000);
        },

        /**
         * Проверить код подтверждения
         */
        confirmCode() {
            if (this.isLoading) return;

            if (this.phone && this.code) {
                this.isLoading = true;
                postForm('/api/user/phone-auth/', new FormData(this.$refs.loginForm))
                    .then((result) => {
                        const isSuccess = result.status === 'success';
                        this.isLoading = false;
                        this.codeStatus = isSuccess ? 'Вход подтверждён' : (result.message || MESSAGES.sendFailed);
                        this.isAuthorized = isSuccess;
                        this.isCodeInvalid = !isSuccess;

                        if (isSuccess) {
                            setTimeout(() => {
                                this.$emit('authorized');

                                const redirectUrl = localStorage.getItem(LAB_REDIRECT_URL);
                                localStorage.removeItem(LAB_REDIRECT_URL);

                                if (redirectUrl) {
                                    window.location = redirectUrl;
                                    return;
                                }

                                if (localStorage.getItem(LABS_SETTINGS.openLabsAfterLoginLSKey)) {
                                    localStorage.removeItem(LABS_SETTINGS.openLabsAfterLoginLSKey);
                                    window.location = LABS_SETTINGS.lkLabsURL;
                                    return;
                                }

                                window.location = this.redirectUrl;
                            }, 500);
                        }
                    })
                    .catch(() => {
                        this.isLoading = false;
                        this.codeStatus = MESSAGES.noConnection;
                    });
            }
        },

        onSubmit(invalid) {
            if (invalid) return;

            if (!this.isCodeSent) {
                this.sendCode();
                return;
            }

            if (!this.codeStatus) {
                this.confirmCode();
            }
        },
    },

    computed: {
        redirectUrl() {
            let redirectURL = '/personal/profile/';
            const url = new URL(window.location.href);
            const hrefParam = url.searchParams.get('href');

            if (hrefParam) {
                redirectURL = hrefParam;
            }

            return redirectURL;
        },

        isShowCodeInput() {
            return this.isCodeSent && this.phoneConfirmed === this.phone;
        },

        sendButtonText() {
            if (this.sendStage === SEND_STAGE.CHECKING) return 'Проверяем…';
            if (this.sendStage === SEND_STAGE.SENDING) return 'Отправляем код…';
            return 'Отправить код';
        },
    },

    mounted() {
        // Форма живёт в окне входа (footer): капча грузится при открытии окна, а не с каждой страницей
        const modal = $(this.$el).closest('.modal').get(0);
        if (!modal) {
            this.prepareCaptcha();
            return;
        }
        this.modalElement = modal;
        $(modal).on('show.bs.modal', this.prepareCaptcha);
    },

    beforeDestroy() {
        clearInterval(this.resendTimer);
        this.clearCaptchaCheckTimer();
        if (this.modalElement) {
            $(this.modalElement).off('show.bs.modal', this.prepareCaptcha);
        }
    },

    watch: {
        isPhoneConfirmed(value) {
            this.$nextTick(() => {
                if (value) this.focusOnCode();
            });
        },
    },
};
</script>
